import React from "react";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "../Icon";

interface FullScreenErrorProps {
  /** Error title */
  title?: string;
  /** Error message/description */
  message?: string;
  /** Retry callback - if provided, shows retry button */
  onRetry?: () => void;
  /** Whether retry is in progress */
  isRetrying?: boolean;
  /** Whether the device is offline */
  isOffline?: boolean;
}

/**
 * Full screen error state.
 * Used when there's an error AND no cached data to show.
 */
export const FullScreenError: React.FC<FullScreenErrorProps> = ({
  title = "Unable to load",
  message = "Please check your connection and try again.",
  onRetry,
  isRetrying = false,
  isOffline = false,
}) => {
  const { theme } = useUnistyles();

  return (
    <View flex={1} bg="background" justifyContent="center" alignItems="center" px="5xl" gap="lg">
      <Icon
        name={"errorOutline"}
        size={theme.spacing["13xl"]}
        color={isOffline ? theme.colors.textMuted : theme.colors.status.error}
      />
      <Text textStyle="lg/semibold" textAlign="center">
        {title}
      </Text>
      <Text textStyle="md/regular" color="textMuted" textAlign="center">
        {isOffline ? "You're offline and there's no cached data available." : message}
      </Text>
      {onRetry && !isOffline && (
        <Button
          label={isRetrying ? "Retrying..." : "Try Again"}
          variant="secondary"
          onPress={onRetry}
          disabled={isRetrying}
        />
      )}
    </View>
  );
};
